import React from 'react';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useHoldRequests } from '@/hooks/useHoldRequests';
import { DashboardMetrics } from '../DashboardMetrics';
import { QuickActions } from '../QuickActions';
import { RecentActivity } from '../RecentActivity';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import { REGION_LIST } from '@/lib/regions';
import type { UserRole } from '@/types';

interface LeadershipDashboardProps {
  role: UserRole;
}

export function LeadershipDashboard({ role }: LeadershipDashboardProps) {
  const { user } = useAuthContext();
  const { data: holdRequestsData, isLoading } = useHoldRequests();

  const roleTitle = React.useMemo(() => {
    switch (role) {
      case 'sales_vp':
        return 'Sales VP Dashboard';
      case 'gvp':
        return 'GVP Dashboard';
      case 'rvp':
        return 'RVP Dashboard';
      case 'adops_leadership':
        return 'AdOps Leadership Dashboard';
      case 'admin':
        return 'Admin Dashboard';
      default:
        return 'Leadership Dashboard';
    }
  }, [role]);

  const roleDescription = React.useMemo(() => {
    switch (role) {
      case 'sales_vp':
        return 'Monitor team performance and hold request metrics.';
      case 'gvp':
        return 'Enterprise-wide visibility across all regions and teams.';
      case 'rvp':
        return 'Regional overview and team performance tracking.';
      case 'adops_leadership':
        return 'Operational oversight and exception approvals.';
      case 'admin':
        return 'System overview with read-only access to all hold requests.';
      default:
        return 'Leadership overview and analytics.';
    }
  }, [role]);

  // Calculate comprehensive metrics from server-provided status counts
  const metrics = React.useMemo(() => {
    if (!holdRequestsData) {
      return {
        activeRequests: 0,
        pendingActions: 0,
        completedToday: 0,
        totalLineItems: 0,
        standardHolds: 0,
        exceptionHolds: 0,
      };
    }

    const counts = holdRequestsData.statusCounts || {};
    const requests = holdRequestsData.holdRequests || [];

    const activeRequests = (counts.open || 0) + (counts.partial_response_received || 0);
    const pendingActions = (counts.partial_response_received || 0) + (counts.needs_review || 0);

    const today = new Date().toDateString();
    const completedToday = requests.filter(
      (r) =>
        r.workflowStatus !== 'cancelled' &&
        r.derivedStatus === 'request_completed_active' &&
        new Date(r.updatedAt).toDateString() === today
    ).length;

    const totalLineItems = requests.reduce((sum, r) => sum + r.totalLineItems, 0);
    const standardHolds = requests.reduce((sum, r) => sum + r.standardHoldsCount, 0);
    const exceptionHolds = requests.reduce((sum, r) => sum + r.exceptionHoldsCount, 0);

    return {
      activeRequests,
      pendingActions,
      completedToday,
      totalLineItems,
      standardHolds,
      exceptionHolds,
    };
  }, [holdRequestsData]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-neutral-900">{roleTitle}</h1>
        <p className="mt-2 text-neutral-600">
          Welcome back, {user?.firstName}! {roleDescription}
        </p>
      </div>

      <DashboardMetrics metrics={metrics} />

      <QuickActions role={role} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Team Performance</CardTitle>
            <CardDescription>Key performance indicators</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-neutral-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-neutral-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-outfront-purple-light rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-900">Avg. Processing Time</p>
                    <p className="text-sm text-neutral-600">Per request</p>
                  </div>
                  <Badge className="bg-outfront-purple-light text-outfront-purple">-</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-900">SLA Compliance</p>
                    <p className="text-sm text-neutral-600">On-time completion</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800">-</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-900">Exception Rate</p>
                    <p className="text-sm text-neutral-600">Exceptions vs standard</p>
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-800">
                    {metrics.exceptionHolds > 0
                      ? `${Math.round((metrics.exceptionHolds / metrics.totalLineItems) * 100)}%`
                      : '0%'}
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Regional Breakdown</CardTitle>
            <CardDescription>Requests by region</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {REGION_LIST.map((region) => (
                <div key={region} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-900">{region}</p>
                    <p className="text-sm text-neutral-600">Active requests</p>
                  </div>
                  <Badge className="bg-neutral-100 text-neutral-900">-</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <RecentActivity requests={holdRequestsData?.holdRequests || []} isLoading={isLoading} />
    </div>
  );
}
